import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ChevronRight, Repeat } from 'lucide-react';
import { TNaerasJobRun } from '@/data/types';
import {
  JOB_META,
  formatAthensDateTime,
  isZeroFilled,
  jobOutcome,
  parseJobDate,
  summariseHepResult,
  summariseResult,
} from '../utils/jobs';

interface JobRunRowProps {
  run: TNaerasJobRun;
  /** The backend's today, so a pending retry can be told from a final failure. */
  todayStr: string;
}

/** Per-group styling for the Job D date lists. */
const GROUP_TONES = {
  success: { box: 'border-emerald-200 bg-emerald-50/70', label: 'text-emerald-900', chip: 'bg-emerald-100 text-emerald-800' },
  warning: { box: 'border-amber-200 bg-amber-50/70', label: 'text-amber-900', chip: 'bg-amber-100 text-amber-900' },
  danger: { box: 'border-rose-200 bg-rose-50/70', label: 'text-rose-900', chip: 'bg-rose-100 text-rose-800' },
} as const;

const TONE_CHIPS = {
  success: 'bg-emerald-100 text-emerald-800',
  warning: 'bg-amber-100 text-amber-900',
  danger: 'bg-rose-100 text-rose-800',
  neutral: 'bg-slate-200 text-slate-800',
} as const;

/** Marks a Job B run that finalizing a bid set off, outside the schedule. */
export const ManualTag: React.FC = () => (
  <span
    title="Εκτελέστηκε κατά την οριστικοποίηση της προσφοράς, εκτός προγράμματος"
    className="flex-shrink-0 rounded-md bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold text-indigo-800"
  >
    Χειροκίνητη
  </span>
);

/** One run, with its `result` folded away until asked for. */
export const JobRunRow: React.FC<JobRunRowProps> = ({ run, todayStr }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  const meta = JOB_META[run.job];
  const title = meta?.title || `Unknown Job (${run.job})`;
  const runDateMeaning = meta?.runDateMeaning || 'Άγνωστη σημασία ημερομηνίας';
  const outcome = jobOutcome(run, todayStr);
  const summary = summariseResult(run);
  const hepGroups = run.job === 'D' ? summariseHepResult(run) : [];
  const startedAt = parseJobDate(run.started_at);
  const updatedAt = parseJobDate(run.updated_at);
  const hasDetail = summary.length > 0 || hepGroups.length > 0 || Boolean(run.error);
  const zeroFilled = isZeroFilled(run);

  return (
    <>
      <tr
        onClick={() => hasDetail && setIsExpanded((prev) => !prev)}
        className={`transition-colors ${
          hasDetail ? 'cursor-pointer hover:bg-blue-50/40' : ''
        } ${isExpanded ? 'bg-blue-50/60' : ''}`}
      >
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <ChevronRight
              className={`h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform ${
                isExpanded ? 'rotate-90' : ''
              } ${hasDetail ? '' : 'invisible'}`}
            />
            <span className="font-bold text-gray-900">
              {format(parseISO(run.run_date), 'dd/MM/yyyy')}
            </span>
          </div>
        </td>

        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <span className="rounded-lg bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-700">
              {run.job}
            </span>
            <span className="text-gray-700">{title}</span>
            {run.manual && <ManualTag />}
          </div>
        </td>

        <td className="py-3 px-4 text-center">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
              TONE_CHIPS[outcome.tone]
            }`}
          >
            {outcome.label}
          </span>
          {zeroFilled && (
            <span
              className="ml-1.5 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600"
              title="Το αποτέλεσμα συμπληρώθηκε με μηδενικά — δεν είναι μετρήσεις"
            >
              Μηδενικά
            </span>
          )}
        </td>

        <td className="py-3 px-4 text-center">
          {run.attempts > 1 ? (
            <span className="inline-flex items-center gap-1 font-bold text-amber-700">
              <Repeat className="h-3 w-3" />
              {run.attempts}
            </span>
          ) : (
            <span className="text-gray-400">1</span>
          )}
        </td>

        <td className="py-3 px-4 text-right font-mono text-gray-600">
          {startedAt ? formatAthensDateTime(startedAt) : '—'}
        </td>

        <td className="py-3 px-4 text-right font-mono text-gray-600">
          {updatedAt ? formatAthensDateTime(updatedAt) : '—'}
        </td>
      </tr>

      {isExpanded && (
        <tr className="bg-blue-50/30">
          <td colSpan={6} className="px-4 pb-4 pt-0">
            <div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
              <p className="mb-3 text-[11px] text-gray-500">{runDateMeaning}</p>

              {run.error && (
                <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
                  {run.error}
                </div>
              )}

              {zeroFilled && (
                <p className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                  Η εκτέλεση απέτυχε και το αποτέλεσμα συμπληρώθηκε με μηδενικά — δεν είναι μετρήσεις
                  και δεν επηρεάζει το απόθεμα ή τις προσφορές. Φαίνονται μόνο οι τιμές που ισχύουν.
                </p>
              )}

              {summary.length > 0 && (
                <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                  {summary.map((entry) => (
                    <div
                      key={entry.label}
                      className="flex items-baseline justify-between gap-3 border-b border-gray-50 pb-1.5"
                    >
                      <dt className="text-[11px] font-semibold text-gray-500">{entry.label}</dt>
                      <dd className="font-mono text-xs font-bold text-gray-900">{entry.value}</dd>
                    </div>
                  ))}
                </dl>
              )}

              {hepGroups.length > 0 && (
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {hepGroups.map((group) => {
                    const tone = GROUP_TONES[group.tone];

                    return (
                      <div key={group.label} className={`rounded-xl border p-3 ${tone.box}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-[11px] font-bold ${tone.label}`}>{group.label}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${tone.chip}`}>
                            {group.values.length}
                          </span>
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {group.values.map((value) => (
                            <span
                              key={value}
                              className="rounded-md bg-white/80 px-1.5 py-0.5 font-mono text-[10px] text-gray-700"
                            >
                              {value}
                            </span>
                          ))}
                        </div>
                        <p className="mt-1.5 text-[10px] text-gray-500">{group.hint}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {run.result && (
                <>
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowRaw((prev) => !prev);
                    }}
                    className="mt-3 rounded-lg bg-gray-100 px-3 py-1 text-[11px] font-semibold text-gray-700 transition hover:bg-gray-200"
                  >
                    {showRaw ? 'Απόκρυψη αναλυτικών' : 'Αναλυτικά δεδομένα'}
                  </button>

                  {showRaw && (
                    <pre className="mt-2 max-h-72 overflow-auto rounded-xl bg-gray-900 p-3 text-[11px] leading-relaxed text-gray-100">
                      {JSON.stringify(run.result, null, 2)}
                    </pre>
                  )}
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default JobRunRow;
